import { useMemo, useState } from 'react';
import { Box, List, ListItemButton, ListItemIcon, Typography, TextField, Stack } from '@mui/material';
import palette from 'theme/palette';
import { useGroupSelectContext } from '../../context';
import { FAIcon } from '@packages/ui-components';

export function OptionsList() {
  const { selectedGroup, handleSelect, groups, values } = useGroupSelectContext();
  const [search, setSearch] = useState('');

  const group = useMemo(() => {
    return groups.find((g) => g.name === selectedGroup);
  }, [groups, selectedGroup]);

  const filteredOptions = useMemo(() => {
    let options = [...group.options];

    if (search.length > 0) {
      options = options.filter((option) => {
        if (option.searchTerms) {
          return option.searchTerms.some((term) => term.toLowerCase().includes(search.toLowerCase()));
        }

        return option.value.toString().toLowerCase().includes(search.toLowerCase());
      });
    }

    return options;
  }, [search, group.options]);

  if (!group) return null;

  return (
    <Stack direction="column" sx={{ flex: 3 }}>
      {group.searchEnabled ? (
        <Box
          sx={{
            zIndex: 1,
            backgroundColor: '#FFF', // Apply background here
            display: 'flex',
            height: 44, // Fixed height
            alignItems: 'center',
            borderBottom: '1px solid #D0D5DD',
            p: '0',
          }}
        >
          <TextField
            placeholder={group.searchPlaceholder ?? 'Type to search'}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            fullWidth
            variant="outlined"
            InputProps={{
              sx: {
                borderRadius: '0px',
                backgroundColor: '#FFF',
                height: 44,
                padding: '0 !important',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none !important',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none !important',
                },

                '& input::placeholder': {
                  color: '#98A2B3',
                  opacity: 1,
                },
                '& input': {
                  padding: '0 10px !important', // Adjust padding inside the input if needed
                  height: 44, // Ensure input element matches container height
                  boxSizing: 'border-box', // Include padding in height calculation
                },
              },
            }}
            // Styles for the root TextField component (less common for visual input styling)
            sx={{
              // Remove the default Mui-focused text color change if it exists
              '& .MuiInputBase-root.Mui-focused': {
                color: 'inherit', // Keep default text color on focus
              },
            }}
          />
        </Box>
      ) : null}

      <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
        {/* Making list absolute in order for it not to stretch our component but rather use scrollbar */}
        <List
          sx={{
            position: 'absolute',
            inset: 0,
            p: 0,
            border: 'none',
            height: '100%',
            backgroundColor: '#fff',
            overflow: 'auto',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '16px',
              backgroundColor: '#fff !important',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
              borderRadius: '8px',
              margin: '4px 0',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#98A2B3',
              borderRadius: '8px',
              border: '6px solid transparent',
              backgroundClip: 'padding-box',
              minHeight: '30px',
            },
            '&::-webkit-scrollbar-thumb:active': {
              backgroundColor: palette.light.grey[500],
            },
          }}
        >
          {filteredOptions.map((option, index, opts) => {
            const isSelected = values[group.name]?.includes(option.value);

            return (
              <ListItemButton
                disableRipple
                disableGutters
                sx={{
                  p: '0 10px',
                  display: 'flex',
                  position: 'relative',
                  gap: '10px',
                  mb: index === opts.length - 1 ? 0 : '10px',
                  justifyContent: 'flex-start',
                  height: '44px',
                  overflow: 'hidden',
                  '&.Mui-selected': {
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: '#000',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: '#000',
                  },
                }}
                key={option.value}
                selected={isSelected}
                onClick={() => {
                  handleSelect(group.name, option.value);
                }}
              >
                {option.icon && (
                  <ListItemIcon sx={{ minWidth: 0 }}>
                    {typeof option.icon === 'string' && <i className={option.icon} style={{ fontSize: '20px', color: '#667085' }} />}
                    {typeof option.icon !== 'string' && option.icon}
                  </ListItemIcon>
                )}

                {group.renderValue ? (
                  group.renderValue(option.value)
                ) : (
                  <Typography
                    sx={{
                      flex: '1 1 0',
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      overflowWrap: 'break-word',
                      color: 'var(--Black, #000)',
                      fontFamily: 'Inter',
                      fontSize: '16px',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      lineHeight: '24px',
                    }}
                  >
                    {option.label}
                  </Typography>
                )}

                {isSelected && (
                  <Box>
                    <FAIcon icon="faCheck" size={16} color="#2055FF" />
                  </Box>
                )}
              </ListItemButton>
            );
          })}
        </List>
      </Box>
    </Stack>
  );
}
